import Map from 'google-map-react';
import React, { useContext, useEffect, useRef } from 'react';
import { styled } from 'linaria/react';
import { LocationStateContext } from './StoreLocator';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import mapMarker from '../../svg/Map-pin.svg'; //'./map-pin.svg';
import { theme } from '../Theme';

const Wrapper = styled('div')`
  height: 50vh;

  .map-container {
    min-height: 100%;
    position: sticky;
    top: 0;
    z-index: 999;
  }

  ${theme.below.lg} {
    height: 50vh;
    position: relative;
  }
`;

const StoreMap = ({ stores, userLocation, closestStore, selectedStoreId }) => {
  const ref = useRef();
  const mapsRef = useRef();
  const markers = useRef();
  const locationState = useContext(LocationStateContext);
  const { googleMapsApiKey } = useShopConfig();

  const zoomedInLevel = 13;
  const zoomedOutLevel = 4;

  const handleMapApiLoaded = (map, maps) => {
    ref.current = map;
    mapsRef.current = maps;
    markers.current = [];
    var infoContainer;

    stores.forEach(store => {
      if (store.id === 25) return;
      let marker = new maps.Marker({
        icon: {
          url: mapMarker,
          size: new maps.Size(42, 42),
          origin: new maps.Point(0, 0),
          anchor: new maps.Point(0, 42)
        },
        position: {
          lat: store.coordinates.latitude,
          lng: store.coordinates.longitude
        },
        map,
        title: store.name
      });
      marker.id = store.id;
      marker.addListener('mouseover', () => {
        locationState.setHighlightedLocation(store.id);
      });

      marker.addListener('mouseout', () => {
        locationState.setHighlightedLocation(null);
      });

      marker.addListener('click', () => {
        if (infoContainer) {
          infoContainer.close();
        }
        infoContainer = new mapsRef.current.InfoWindow({
          content: `<div style={{background: "white",padding: "1em",minWidth: "200px",fontSize: "14px",lineHeight: 1.5, border-radius:0}}><p><strong>${store.name}</strong></p><p>${store.address1}</p><p>${store.city}</p></div>`
        });
        infoContainer.open(map, marker);
      });

      markers.current.push(marker);
    });
  };

  useEffect(() => {
    if (selectedStoreId) {
      const map = ref.current;
      let marker = markers.current.filter(
        marker => marker.id === selectedStoreId
      )[0];
      map.setCenter(marker.position);
      map.setZoom(zoomedInLevel);
      mapsRef.current.event.trigger(marker, 'click');
    }
  }, [selectedStoreId]);

  useEffect(() => {
    if (userLocation.latitude) {
      const map = ref.current;
      if (userLocation.latitude && userLocation.longitude) {
        map.setCenter({
          lat: userLocation.latitude,
          lng: userLocation.longitude
        });
        map.setZoom(zoomedInLevel);
      }
    }
  }, [userLocation.latitude, userLocation.longitude]);

  return (
    <Wrapper>
      <>
        <Map
          bootstrapURLKeys={{
            key: googleMapsApiKey
          }}
          defaultZoom={userLocation.latitude ? zoomedInLevel : zoomedOutLevel}
          defaultCenter={
            userLocation.latitude
              ? { lat: userLocation.latitude, lng: userLocation.longitude }
              : { lat: 64.024104, lng: 12.65163 }
          }
          options={{
            styles: mapStyle,
            streetViewControl: 'true'
          }}
          yesIWantToUseGoogleMapApiInternals={true}
          onGoogleApiLoaded={({ map, maps }) => handleMapApiLoaded(map, maps)}
        />
      </>
    </Wrapper>
  );
};

const mapStyle = [
  {
    featureType: 'all',
    elementType: 'labels.text.fill',
    stylers: [
      {
        saturation: 36
      },
      {
        color: '#333333'
      },
      {
        lightness: 40
      }
    ]
  },
  {
    featureType: 'all',
    elementType: 'labels.text.stroke',
    stylers: [
      {
        visibility: 'on'
      },
      {
        color: '#ffffff'
      },
      {
        lightness: 16
      }
    ]
  },
  {
    featureType: 'all',
    elementType: 'labels.icon',
    stylers: [
      {
        visibility: 'off'
      }
    ]
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff'
      },
      {
        lightness: 20
      }
    ]
  },
  {
    featureType: 'administrative',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#fefefe'
      },
      {
        lightness: 17
      },
      {
        weight: 1.2
      }
    ]
  },
  {
    featureType: 'landscape',
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5'
      },
      {
        lightness: 20
      }
    ]
  },
  {
    featureType: 'landscape.man_made',
    elementType: 'geometry',
    stylers: [
      {
        color: '#e6e6e6'
      }
    ]
  },
  {
    featureType: 'poi',
    elementType: 'geometry',
    stylers: [
      {
        color: '#f5f5f5'
      },
      {
        lightness: 21
      }
    ]
  },
  {
    featureType: 'poi.park',
    elementType: 'geometry',
    stylers: [
      {
        color: '#dadada'
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#acacac'
      },
      {
        lightness: 17
      }
    ]
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [
      {
        lightness: 29
      },
      {
        weight: 0.2
      },
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry',
    stylers: [
      {
        color: '#bcbcbc'
      },
      {
        lightness: 18
      }
    ]
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#f3f3f3'
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'geometry',
    stylers: [
      {
        color: '#ffffff'
      },
      {
        lightness: 16
      }
    ]
  },
  {
    featureType: 'road.local',
    elementType: 'geometry.stroke',
    stylers: [
      {
        color: '#d7d7d7'
      }
    ]
  },
  {
    featureType: 'transit.line',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff'
      }
    ]
  },
  {
    featureType: 'transit.station.airport',
    elementType: 'labels.icon',
    stylers: [
      {
        color: '#000000'
      }
    ]
  },
  {
    featureType: 'transit.station.bus',
    elementType: 'labels.icon',
    stylers: [
      {
        color: '#ff0000'
      }
    ]
  },
  {
    featureType: 'transit.station.rail',
    elementType: 'labels.icon',
    stylers: [
      {
        color: '#000000'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [
      {
        color: '#c4c4c4'
      },
      {
        lightness: 17
      }
    ]
  }
];

export default StoreMap;
